import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button, Card, Input, Label, Alert } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { useBranding } from "../branding/BrandingProvider";

// v43.35.9 — accept only INTERNAL portal paths as post-login return targets.
// Rejects absolute URLs (http://, //evil.com), protocol-relative paths,
// and anything not starting with a single "/". Prevents open-redirect abuse
// where a phishing email crafts /login?from=https://attacker.example.
function _safeReturnTo(candidate) {
  if (typeof candidate !== "string") return null;
  if (candidate.length === 0 || candidate.length > 512) return null;
  if (!candidate.startsWith("/")) return null;
  if (candidate.startsWith("//")) return null;      // protocol-relative
  if (candidate.startsWith("/\\")) return null;     // reversed slash tricks
  if (/^\/[a-zA-Z]+:/.test(candidate)) return null; // /http:...
  return candidate;
}

export default function LoginPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { branding } = useBranding();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false); // v43.35.13 trusted device
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  // v34.9 — Self-service password recovery inline panel
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState(null);
  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotMsg(null); setForgotBusy(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const r = await axios.post(`${API}/api/auth/forgot-password`,
        { email: forgotEmail });
      setForgotMsg(r?.data?.message ||
        "Se l'indirizzo è associato a un account attivo, riceverai un link per reimpostare la password.");
    } catch (e) {
      setForgotMsg("Se l'indirizzo è associato a un account attivo, riceverai un link per reimpostare la password.");
    } finally { setForgotBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const me = await login(email.trim(), password, rememberMe);
      const roles = me?.roles || [];
      // v43.35.9 — honor a validated ?from= deep-link so email CTAs like
      // /customer/orders/{uuid} return to the intended page after login.
      const returnTo = _safeReturnTo(location.state?.from);
      if (returnTo) { nav(returnTo, { replace: true }); return; }
      if (roles.includes("ADMIN")) nav("/admin");
      else if (roles.includes("SALES_OPERATOR")) nav("/sales");
      else if (roles.includes("LOGISTICS_OPERATOR")) nav("/logistics");
      else nav("/customer");
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Credenziali non valide");
    } finally { setBusy(false); }
  };

  const bg = branding?.login_background_url;
  const heroStyle = bg
    ? { backgroundImage: `linear-gradient(135deg, rgba(0,0,0,.45), rgba(0,0,0,.15)), url(${bg})`, backgroundSize: "cover", backgroundPosition: "center" }
    : {};

  return (
    <div className="login-page">
      <aside className="login-hero" style={heroStyle}>
        <div className="login-hero-inner">
          <div className="kicker" style={{ marginBottom: "1rem" }}>Portale B2B</div>
          <h1>{branding?.app_name || "Portale Ordini"}</h1>
          <p>
            Accedi al tuo listino personalizzato, gestisci ordini e consegne,
            consulta schede tecniche e monitora il tuo PFA.
          </p>
        </div>
      </aside>

      <div className="login-form-wrap">
        <Card className="login-form-card" tone="elevated">
          <Card.Header>
            <div className="app-brand" style={{ justifyContent: "flex-start" }}>
              {branding?.logo_compact_url
                ? <img src={branding.logo_compact_url} alt="" style={{ height: 34 }} />
                : <span className="app-brand-mark">B</span>}
              <span>{branding?.app_name || "Portale Ordini GE SURGELATI"}</span>
            </div>
          </Card.Header>
          <Card.Body>
            <form onSubmit={submit} className="stack-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email" type="text" autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="login-password" type="password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8,
                              fontSize: ".85rem", cursor: "pointer" }}
                     data-testid="login-remember-me-label">
                <input type="checkbox" checked={rememberMe}
                       onChange={(e) => setRememberMe(e.target.checked)}
                       data-testid="login-remember-me"
                       style={{ marginTop: 3 }} />
                <span>
                  <strong>Resta connesso su questo dispositivo</strong>
                  <div className="muted" style={{ fontSize: ".78rem", marginTop: 2 }}>
                    Usalo solo su un computer personale o aziendale non condiviso.
                  </div>
                </span>
              </label>
              {err && <Alert tone="danger">{err}</Alert>}
              <Button data-testid="login-submit" type="submit" variant="primary" size="lg" full loading={busy} disabled={busy}>
                {busy ? "Accesso in corso…" : "Accedi"}
              </Button>
              <div style={{ textAlign: "center", marginTop: 6 }}>
                <button type="button"
                        onClick={() => { setShowForgot(!showForgot); setForgotMsg(null); }}
                        data-testid="login-forgot-toggle"
                        style={{ background: "none", border: "none", color: "#78350F",
                                 textDecoration: "underline", cursor: "pointer", fontSize: ".88rem" }}>
                  Password dimenticata?
                </button>
              </div>
            </form>
            {showForgot && (
              <div style={{ marginTop: 14, padding: 14, background: "#FEF3C7",
                            border: "1px solid #F59E0B", borderRadius: 8 }}
                   data-testid="login-forgot-panel">
                <h3 style={{ margin: "0 0 6px", fontSize: "1rem" }}>Recupera accesso</h3>
                <p className="muted" style={{ fontSize: ".85rem", margin: "0 0 10px" }}>
                  Inserisci la tua email: se corrisponde a un account attivo, ti invieremo un link per impostare una nuova password.
                </p>
                {forgotMsg ? (
                  <div data-testid="login-forgot-msg"
                       style={{ padding: 8, background: "#fff", borderRadius: 6,
                                border: "1px solid #E5E7EB", fontSize: ".88rem" }}>
                    {forgotMsg}
                  </div>
                ) : (
                  <form onSubmit={submitForgot} className="stack-2">
                    <Input type="email" placeholder="tuo@indirizzo.email"
                           value={forgotEmail} data-testid="login-forgot-email"
                           onChange={(e) => setForgotEmail(e.target.value)} required />
                    <Button type="submit" variant="primary" full disabled={forgotBusy}
                            data-testid="login-forgot-submit">
                      {forgotBusy ? "Invio…" : "Invia link di reimpostazione"}
                    </Button>
                  </form>
                )}
              </div>
            )}
          </Card.Body>
          <Card.Footer>
            <div className="muted" style={{ fontSize: ".8rem" }}>
              Ambiente DEMO — usa le credenziali fornite dal tuo agente.
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
}
